import {
  format as formatDate,
  formatDistance,
  formatDistanceStrict,
  formatRelative,
  isDate,
  isValid,
  Locale,
} from "date-fns";

interface ShortDateDistanceProps {
  lessThanXSeconds: string;
  xSeconds: string;
  halfAMinute: string;
  lessThanXMinutes: string;
  xMinutes: string;
  aboutXHours: string;
  xHours: string;
  xDays: string;
  aboutXWeeks: string;
  xWeeks: string;
  aboutXMonths: string;
  xMonths: string;
  aboutXYears: string;
  xYears: string;
  overXYears: string;
  almostXYears: string;
}

interface FormatRelativeProps {
  lastWeek: string;
  yesterday: string;
  today: string;
  tomorrow: string;
  nextWeek: string;
  other: string;
}

export interface DateResource {
  translation: {
    shortDateDistance: ShortDateDistanceProps;
    formatRelative: FormatRelativeProps;
    formatRelativeShort: FormatRelativeProps;
  };
}

type propKeys = keyof ShortDateDistanceProps;
interface DateValue {
  start: Date;
  end: Date;
}
export const formatIntlDate = (
  value: Date | DateValue,
  format: string,
  locale: Locale,
  resource: DateResource,
) => {
  const startDate = isDate(value) ? value : value.start;
  const endDate = isDate(value) ? value : value.end;
  switch (format) {
    case "short":
      return formatDate(startDate, "P", { locale });
    case "input":
      return formatDate(startDate, "yyyy-MM-dd", { locale });
    case "long":
      return formatDate(startDate, "PPPP", { locale });
    case "relative": {
      locale = {
        ...locale,
        formatRelative: (token: keyof FormatRelativeProps) =>
          resource.translation.formatRelative[token],
      };
      return formatRelative(startDate, new Date(), { locale });
    }
    case "relative_short": {
      locale = {
        ...locale,
        formatRelative: (token: keyof FormatRelativeProps) =>
          resource.translation.formatRelativeShort[token],
      };
      return formatRelative(startDate, new Date(), { locale });
    }
    case "distance":
      return isValid(startDate) && isValid(endDate)
        ? formatDistanceStrict(startDate, endDate, {
            locale: {
              ...locale,
              formatDistance: (token, count) =>
                formatShortDistance(token, count, resource),
            },
          })
        : "";
    case "monthday":
      return formatDate(startDate, "LLL do", { locale });
    case "monthdayyear":
      return formatDate(startDate, "LLL d, yyyy", { locale });
    case "monthyear":
      return formatDate(startDate, "LLL, yyyy", { locale });
    case "daymonth":
      return formatDate(startDate, "d. LLL", { locale });
    case "monthday_short":
      return formatDate(startDate, "L/d", { locale });
    case "daymonth_short":
      return formatDate(startDate, "d.L", { locale });
    case "time":
      return formatDate(startDate, "p", { locale });
    case "ago":
      return formatDistance(startDate, new Date(), {
        locale,
        addSuffix: true,
      });
    default:
      return formatDate(startDate, format, { locale });
  }
};

export function formatShortDistance(
  token: propKeys,
  count: number,
  resource: DateResource,
) {
  const result = resource.translation.shortDateDistance[token]?.replace(
    "{{count}}",
    `${count}`,
  );
  return result;
}
